import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApprovalWorkflow, useDeleteConfirmState } from '@/components/hooks';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, FileText, Upload, ExternalLink, CheckCircle, Clock, Plus, Edit2, Trash2, X, ThumbsUp, Award, History, LayoutGrid, TableProperties, Archive, ArchiveRestore } from 'lucide-react';
import { format } from 'date-fns';
import ArtifactVersionHistory from '../components/artifacts/ArtifactVersionHistory';
import ArtifactSearch from '../components/artifacts/ArtifactSearch';
import RitualSearch from '../components/artifacts/RitualSearch';
import ContextualDocuments from '@/components/documents/ContextualDocuments';
import ArtifactApprovalQueue from '@/components/commander/ArtifactApprovalQueue';
import ArtifactTableView from '@/components/artifacts/ArtifactTableView';

export default function ArtifactsPage() {
  const queryClient = useQueryClient();
  const [artifactDialog, setArtifactDialog] = useState(false);
  const [ritualDialog, setRitualDialog] = useState(false);
  const [editingArtifact, setEditingArtifact] = useState(null);
  const [editingRitual, setEditingRitual] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const deleteConfirm = useDeleteConfirmState({ confirmTimeout: 3000 });
  const [versionHistoryArtifact, setVersionHistoryArtifact] = useState(null);
  const [filteredArtifacts, setFilteredArtifacts] = useState([]);
  const [filteredRituals, setFilteredRituals] = useState([]);
  const [artifactViewMode, setArtifactViewMode] = useState('card');
  const [showArchivedArtifacts, setShowArchivedArtifacts] = useState(false);
  const [showArchivedRituals, setShowArchivedRituals] = useState(false);

  const [artifactForm, setArtifactForm] = useState({
    name: '',
    description: '',
    category: 'other',
    type: 'uploaded',
    file_url: '',
    internal_page: '',
    tags: [],
    editors: [],
    viewers: [],
    owner_email: null,
    is_company_secret: false,
    change_description: '',
    save_version_history: true
  });

  const [ritualForm, setRitualForm] = useState({
    name: '',
    description: '',
    type: 'meeting',
    cadence: 'weekly',
    custom_cadence: '',
    artifact_ids: [],
    game_mode_usage: {
      rank_and_file: 'required',
      campaign: 'optional',
      special_ops: 'not_applicable'
    },
    participants: [],
    duration_minutes: 30,
    owner_email: null,
    is_company_secret: false
  });

  const [currentTag, setCurrentTag] = useState('');
  const [currentParticipant, setCurrentParticipant] = useState('');

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: profile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === user?.email);
    },
    enabled: !!user
  });

  const isSeniorOfficer = user?.role === 'admin' || profile?.custom_role === 'Commander' || profile?.custom_role === 'Officer';
  const isCommanderInChief = profile?.custom_role === 'Commander in Chief';

  const { data: officers } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const { data: artifacts } = useQuery({
    queryKey: ['artifacts'],
    queryFn: () => base44.entities.Artifact.list(),
    initialData: []
  });

  const { data: rituals } = useQuery({
    queryKey: ['rituals'],
    queryFn: () => base44.entities.Ritual.list(),
    initialData: []
  });

  useEffect(() => {
    if (artifacts) {
      setFilteredArtifacts(artifacts);
    }
  }, [artifacts]);

  useEffect(() => {
    if (rituals) {
      setFilteredRituals(rituals);
    }
  }, [rituals]);

  const createArtifactMutation = useMutation({
    mutationFn: (data) => base44.entities.Artifact.create({
      ...data,
      owner_email: user?.email,
      status: 'draft'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      setArtifactDialog(false);
      resetArtifactForm();
    }
  });

  const updateArtifactMutation = useMutation({
    mutationFn: async ({ id, data, changeDescription, saveVersionHistory }) => {
      // Only create version history if user opts in
      if (saveVersionHistory) {
        // Get current artifact state
        const currentArtifact = artifacts.find((a) => a.id === id);
        
        // Get existing versions to determine next version number
        const allVersions = await base44.entities.ArtifactVersion.list();
        const artifactVersions = allVersions.filter((v) => v.artifact_id === id);
        const nextVersion = artifactVersions.length > 0 
          ? Math.max(...artifactVersions.map((v) => v.version_number)) + 1 
          : 1;

        // Create version snapshot of current state before updating
        await base44.entities.ArtifactVersion.create({
          artifact_id: id,
          version_number: nextVersion,
          name: currentArtifact.name,
          description: currentArtifact.description,
          category: currentArtifact.category,
          type: currentArtifact.type,
          file_url: currentArtifact.file_url,
          internal_page: currentArtifact.internal_page,
          tags: currentArtifact.tags,
          editors: currentArtifact.editors,
          viewers: currentArtifact.viewers,
          owner_email: currentArtifact.owner_email,
          status: currentArtifact.status,
          changed_by: user?.email,
          change_description: changeDescription
        });
      }

      // Now update the artifact (track who made the update)
      return base44.entities.Artifact.update(id, { ...data, last_updated_by: user?.email });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
      queryClient.invalidateQueries({ queryKey: ['artifactVersions'] });
      setArtifactDialog(false);
      setEditingArtifact(null);
      resetArtifactForm();
    }
  });

  const deleteArtifactMutation = useMutation({
    mutationFn: (id) => base44.entities.Artifact.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['artifacts'] })
  });

  const archiveArtifactMutation = useMutation({
    mutationFn: async (id) => {
      return base44.entities.Artifact.update(id, {
        archived: true,
        archived_at: new Date().toISOString(),
        archived_by: user?.email
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['artifacts'] })
  });

  const unarchiveArtifactMutation = useMutation({
    mutationFn: async (id) => {
      return base44.entities.Artifact.update(id, {
        archived: false,
        archived_at: null,
        archived_by: null
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['artifacts'] })
  });

  // Approval workflows using refactored hooks
  const artifactWorkflow = useApprovalWorkflow({
    entityType: 'Artifact',
    userEmail: user?.email
  });

  const ritualWorkflow = useApprovalWorkflow({
    entityType: 'Ritual',
    userEmail: user?.email
  });

  const createRitualMutation = useMutation({
    mutationFn: (data) => base44.entities.Ritual.create({
      ...data,
      owner_email: user?.email,
      status: 'draft'
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rituals'] });
      setRitualDialog(false);
      resetRitualForm();
    }
  });

  const updateRitualMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Ritual.update(id, { ...data, last_updated_by: user?.email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rituals'] });
      setRitualDialog(false);
      setEditingRitual(null);
      resetRitualForm();
    }
  });

  const deleteRitualMutation = useMutation({
    mutationFn: (id) => base44.entities.Ritual.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rituals'] })
  });

  const archiveRitualMutation = useMutation({
    mutationFn: async (id) => {
      return base44.entities.Ritual.update(id, {
        archived: true,
        archived_at: new Date().toISOString(),
        archived_by: user?.email
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rituals'] })
  });

  const unarchiveRitualMutation = useMutation({
    mutationFn: async (id) => {
      return base44.entities.Ritual.update(id, {
        archived: false,
        archived_at: null,
        archived_by: null
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rituals'] })
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    setUploadSuccess(false);
    try {
      const uploadResponse = await base44.functions.invoke('uploadToGoogleDrive', {
        file: file,
        context_type: 'Artifact',
        parent_context_id: editingArtifact?.id
      });

      if (!uploadResponse.data.success) {
        throw new Error(uploadResponse.data.error || 'Unknown error');
      }
      
      const uploadedDocument = uploadResponse.data.document;

      setArtifactForm(prev => ({
        ...prev,
        file_url: uploadedDocument.drive_web_link,
        original_file_name: uploadedDocument.name,
        mime_type: uploadedDocument.file_type
      }));
      setUploadSuccess(true);
    } catch (error) {
      alert(`Failed to upload file: ${error.message}`);
      console.error('Upload error:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const resetArtifactForm = () => {
    setArtifactForm({
      name: '',
      description: '',
      category: 'other',
      type: 'uploaded',
      file_url: '',
      original_file_name: '',
      mime_type: '',
      internal_page: '',
      tags: [],
      editors: [],
      viewers: [],
      owner_email: null,
      is_company_secret: false,
      change_description: '',
      save_version_history: true
    });
  };

  const resetRitualForm = () => {
    setRitualForm({
      name: '',
      description: '',
      type: 'meeting',
      cadence: 'weekly',
      custom_cadence: '',
      artifact_ids: [],
      game_mode_usage: {
        rank_and_file: 'required',
        campaign: 'optional',
        special_ops: 'not_applicable'
      },
      participants: [],
      duration_minutes: 30,
      owner_email: null,
      is_company_secret: false
    });
  };

  const handleArtifactSubmit = (e) => {
    e.preventDefault();
    if (editingArtifact) {
      const updateData = { ...artifactForm };
      const changeDescription = updateData.change_description;
      const saveVersionHistory = updateData.save_version_history;
      delete updateData.change_description;
      delete updateData.save_version_history;
      delete updateData.original_file_name;
      delete updateData.mime_type;
      
      if (updateData.owner_email === editingArtifact.owner_email) {
        delete updateData.owner_email;
      }
      updateArtifactMutation.mutate({ 
        id: editingArtifact.id, 
        data: updateData,
        changeDescription: changeDescription || 'Updated artifact',
        saveVersionHistory
      });
    } else {
      const createData = { ...artifactForm };
      delete createData.change_description;
      delete createData.save_version_history;
      delete createData.original_file_name;
      delete createData.mime_type;
      createArtifactMutation.mutate(createData);
    }
  };

  const handleRitualSubmit = (e) => {
    e.preventDefault();
    if (editingRitual) {
      const updateData = { ...ritualForm };
      if (updateData.owner_email === editingRitual.owner_email) {
        delete updateData.owner_email;
      }
      updateRitualMutation.mutate({ id: editingRitual.id, data: updateData });
    } else {
      createRitualMutation.mutate(ritualForm);
    }
  };

  const handleEditArtifact = (artifact) => {
    setEditingArtifact(artifact);
    setUploadSuccess(false);
    setArtifactForm({
      name: artifact.name,
      description: artifact.description || '',
      category: artifact.category,
      type: artifact.type,
      file_url: artifact.file_url || '',
      original_file_name: artifact.original_file_name || '',
      mime_type: artifact.mime_type || '',
      internal_page: artifact.internal_page || '',
      tags: artifact.tags || [],
      editors: artifact.editors || [],
      viewers: artifact.viewers || [],
      owner_email: artifact.owner_email,
      is_company_secret: artifact.is_company_secret || false,
      change_description: '',
      save_version_history: true
    });
    setArtifactDialog(true);
  };

  const handleEditRitual = (ritual) => {
    setEditingRitual(ritual);
    setRitualForm({
      name: ritual.name,
      description: ritual.description || '',
      type: ritual.type || 'meeting',
      cadence: ritual.cadence,
      custom_cadence: ritual.custom_cadence || '',
      artifact_ids: ritual.artifact_ids || [],
      game_mode_usage: ritual.game_mode_usage || {
        rank_and_file: 'required',
        campaign: 'optional',
        special_ops: 'not_applicable'
      },
      participants: ritual.participants || [],
      duration_minutes: ritual.duration_minutes || 30,
      owner_email: ritual.owner_email,
      is_company_secret: ritual.is_company_secret || false
    });
    setRitualDialog(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-slate-500/20 text-slate-400',
      pending_approval: 'bg-amber-500/20 text-amber-400',
      approved: 'bg-emerald-500/20 text-emerald-400',
      institutionalized: 'bg-indigo-500/20 text-indigo-400'
    };
    return <Badge className={`${colors[status]} border-none`}>{status.replace('_', ' ')}</Badge>;
  };

  const visibleArtifacts = filteredArtifacts.filter(a => showArchivedArtifacts ? a.archived : !a.archived);
  const archivedArtifactCount = artifacts.filter(a => a.archived).length;

  const visibleRituals = filteredRituals.filter(r => showArchivedRituals ? r.archived : !r.archived);
  const archivedRitualCount = rituals.filter(r => r.archived).length;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold text-white mb-2">Artifacts & Rituals</h1>
        <p className="text-slate-400">Manage organizational artifacts and establish rituals for consistent execution</p>
      </div>

      {isSeniorOfficer && (
        <ArtifactApprovalQueue 
          artifacts={artifacts} 
          onApprove={(id) => artifactWorkflow.approve(id)}
          onReject={(id) => artifactWorkflow.reject(id)}
        />
      )}

      <Tabs defaultValue="artifacts" className="space-y-6">
        <TabsList className="bg-slate-900/50 border border-slate-800">
          <TabsTrigger value="artifacts">
            <FileText className="w-4 h-4 mr-2" />
            Artifacts
          </TabsTrigger>
          <TabsTrigger value="rituals">
            <Calendar className="w-4 h-4 mr-2" />
            Rituals
          </TabsTrigger>
        </TabsList>

        {/* ARTIFACTS TAB */}
        <TabsContent value="artifacts" className="space-y-4">
          <ArtifactSearch
            artifacts={artifacts}
            officers={officers}
            onFilteredResults={setFilteredArtifacts}
          />

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <p className="text-slate-400 text-sm">
                Showing {visibleArtifacts.length} of {artifacts.length} artifact{artifacts.length !== 1 ? 's' : ''}
              </p>
              <Button
                variant={showArchivedArtifacts ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchivedArtifacts(!showArchivedArtifacts)}
                className={`h-7 text-xs ${showArchivedArtifacts ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-700 text-slate-400'}`}
              >
                <Archive className="w-3 h-3 mr-1" />
                Archived ({archivedArtifactCount})
              </Button>
              <div className="flex items-center border border-slate-700 rounded-md overflow-hidden">
                <button
                  onClick={() => setArtifactViewMode('card')}
                  className={`p-1.5 transition-colors ${artifactViewMode === 'card' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                  title="Card view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setArtifactViewMode('table')}
                  className={`p-1.5 transition-colors ${artifactViewMode === 'table' ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                  title="Table view"
                >
                  <TableProperties className="w-4 h-4" />
                </button>
              </div>
            </div>
            <ConfirmableDialog open={artifactDialog} onOpenChange={(open) => {
              setArtifactDialog(open);
              if (!open) {
                setEditingArtifact(null);
                resetArtifactForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  New Artifact
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="art-artifacts-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingArtifact ? 'Edit Artifact' : 'Create New Artifact'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleArtifactSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input data-testid="art-artifacts-input"
                      value={artifactForm.name}
                      onChange={(e) => setArtifactForm({ ...artifactForm, name: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea data-testid="art-artifacts-textarea"
                      value={artifactForm.description}
                      onChange={(e) => setArtifactForm({ ...artifactForm, description: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={artifactForm.category} onValueChange={(value) => setArtifactForm({ ...artifactForm, category: value })}>
                        <SelectTrigger className="bg-slate-950 border-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="funnel">Funnel</SelectItem>
                          <SelectItem value="dashboard">Dashboard</SelectItem>
                          <SelectItem value="calendar">Calendar</SelectItem>
                          <SelectItem value="protocol">Protocol</SelectItem>
                          <SelectItem value="sop">SOP</SelectItem>
                          <SelectItem value="template">Template</SelectItem>
                          <SelectItem value="framework">Framework</SelectItem>
                          <SelectItem value="checklist">Checklist</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={artifactForm.type} onValueChange={(value) => setArtifactForm({ ...artifactForm, type: value })}>
                        <SelectTrigger className="bg-slate-950 border-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="uploaded">Uploaded File</SelectItem>
                          <SelectItem value="link">External Link</SelectItem>
                          <SelectItem value="internal">Internal (This App)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {artifactForm.type === 'uploaded' && (
                    <div className="space-y-2">
                      <Label>Upload File</Label>
                      {editingArtifact && artifactForm.file_url && (
                        <div className="p-3 bg-slate-950 border border-slate-800 rounded-lg mb-2">
                          <div className="text-xs text-slate-500 mb-1">Current File:</div>
                          <a 
                            href={artifactForm.file_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-indigo-400 hover:text-indigo-300 underline flex items-center gap-2"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Current File
                          </a>
                          <div className="text-xs text-slate-600 mt-1">Upload a new file to replace it</div>
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        <Input data-testid="art-artifacts-input-1"
                          type="file"
                          onChange={handleFileUpload}
                          className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                          disabled={uploadingFile}
                        />
                        {uploadingFile && (
                          <div className="flex items-center gap-2 text-sm text-indigo-400">
                            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin"></div>
                            <span>Uploading to Google Drive...</span>
                          </div>
                        )}
                        {uploadSuccess && !uploadingFile && (
                          <p className="text-xs text-emerald-400 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            File uploaded successfully to Google Drive
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {artifactForm.type === 'link' && (
                    <div className="space-y-2">
                      <Label>External Link</Label>
                      <Input data-testid="art-artifacts-input-2"
                        value={artifactForm.file_url}
                        onChange={(e) => setArtifactForm({ ...artifactForm, file_url: e.target.value })}
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                        placeholder="https://..."
                      />
                    </div>
                  )}

                  {artifactForm.type === 'internal' && (
                    <div className="space-y-2">
                      <Label>Internal Page</Label>
                      <Input data-testid="art-artifacts-input-3"
                        value={artifactForm.internal_page}
                        onChange={(e) => setArtifactForm({ ...artifactForm, internal_page: e.target.value })}
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                        placeholder="e.g., Dashboard, Framework"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>#descriptors</Label>
                    <div className="flex flex-wrap gap-2 p-3 bg-slate-950 border border-slate-800 rounded-lg min-h-[42px]">
                      {artifactForm.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-indigo-500/10 text-indigo-400">
                          #{tag}
                          <button
                            type="button"
                            onClick={() => setArtifactForm({
                              ...artifactForm,
                              tags: artifactForm.tags.filter((_, i) => i !== idx)
                            })}
                            className="ml-1 hover:text-red-400"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input data-testid="art-artifacts-input-4"
                        value={currentTag}
                        onChange={(e) => setCurrentTag(e.target.value)}
                        placeholder="Add a descriptor..."
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (currentTag.trim()) {
                              setArtifactForm({
                                ...artifactForm,
                                tags: [...artifactForm.tags, currentTag.trim()]
                              });
                              setCurrentTag('');
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        onClick={() => {
                          if (currentTag.trim()) {
                            setArtifactForm({
                              ...artifactForm,
                              tags: [...artifactForm.tags, currentTag.trim()]
                            });
                            setCurrentTag('');
                          }
                        }}
                      >
                        Add
                      </Button>
                    </div>
                  </div>

                  {editingArtifact && (editingArtifact.owner_email === user?.email || isCommanderInChief) && (
                    <div className="space-y-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <Label className="text-amber-400">Transfer Ownership</Label>
                      <Select
                        value={artifactForm.owner_email || editingArtifact.owner_email}
                        onValueChange={(value) => {
                          setArtifactForm({
                            ...artifactForm,
                            owner_email: value
                          });
                        }}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800">
                          <SelectValue placeholder="Select new owner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={editingArtifact.owner_email}>
                            {officers.find((o) => o.user_email === editingArtifact.owner_email)?.callsign || editingArtifact.owner_email} (Current)
                          </SelectItem>
                          {officers.filter((o) => o.user_email !== editingArtifact.owner_email).map((officer) => (
                            <SelectItem key={officer.id} value={officer.user_email}>
                              {officer.callsign}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-amber-400">⚠️ Transferring ownership will give full control to the new owner</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Editors (Can Edit)</Label>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (value && !artifactForm.editors.includes(value) && value !== user?.email) {
                          setArtifactForm({
                            ...artifactForm,
                            editors: [...artifactForm.editors, value]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue placeholder="Add editor..." />
                      </SelectTrigger>
                      <SelectContent>
                        {officers.filter((o) => o.user_email !== user?.email && !artifactForm.editors.includes(o.user_email)).map((officer) => (
                          <SelectItem key={officer.id} value={officer.user_email}>
                            {officer.callsign}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2">
                      {artifactForm.editors.map((email) => {
                        const officer = officers.find((o) => o.user_email === email);
                        return (
                          <Badge key={email} variant="secondary" className="bg-emerald-500/10 text-emerald-400">
                            {officer?.callsign || email}
                            <button
                              type="button"
                              onClick={() => setArtifactForm({
                                ...artifactForm,
                                editors: artifactForm.editors.filter((e) => e !== email)
                              })}
                              className="ml-1 hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Viewers (View Only)</Label>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (value && !artifactForm.viewers.includes(value) && !artifactForm.editors.includes(value) && value !== user?.email) {
                          setArtifactForm({
                            ...artifactForm,
                            viewers: [...artifactForm.viewers, value]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue placeholder="Add viewer..." />
                      </SelectTrigger>
                      <SelectContent>
                        {officers.filter((o) =>
                          o.user_email !== user?.email &&
                          !artifactForm.viewers.includes(o.user_email) &&
                          !artifactForm.editors.includes(o.user_email)
                        ).map((officer) => (
                          <SelectItem key={officer.id} value={officer.user_email}>
                            {officer.callsign}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2">
                      {artifactForm.viewers.map((email) => {
                        const officer = officers.find((o) => o.user_email === email);
                        return (
                          <Badge key={email} variant="secondary" className="bg-blue-500/10 text-blue-400">
                            {officer?.callsign || email}
                            <button
                              type="button"
                              onClick={() => setArtifactForm({
                                ...artifactForm,
                                viewers: artifactForm.viewers.filter((v) => v !== email)
                              })}
                              className="ml-1 hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  {isCommanderInChief && (
                    <div className="space-y-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-start gap-3">
                        <input data-testid="art-artifacts-input-5"
                          type="checkbox"
                          id="is_company_secret"
                          checked={artifactForm.is_company_secret}
                          onChange={(e) => setArtifactForm({ ...artifactForm, is_company_secret: e.target.checked })}
                          className="mt-1 w-4 h-4 text-red-600 bg-slate-950 border-slate-700 rounded"
                        />
                        <div className="flex-1">
                          <Label htmlFor="is_company_secret" className="text-sm font-medium cursor-pointer text-red-400">
                            Company Secret
                          </Label>
                          <p className="text-xs text-red-300 mt-1">
                            If enabled, only you (owner), editors, and viewers can see this artifact. It will be hidden from everyone else.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {editingArtifact && (
                    <div className="space-y-3 p-4 bg-indigo-500/5 border border-indigo-500/20 rounded-lg">
                      <div className="flex items-start gap-3">
                        <input data-testid="art-artifacts-input-6"
                          type="checkbox"
                          id="save_version_history"
                          checked={artifactForm.save_version_history}
                          onChange={(e) => setArtifactForm({ ...artifactForm, save_version_history: e.target.checked })}
                          className="mt-1 w-4 h-4 text-indigo-600 bg-slate-950 border-slate-700 rounded"
                        />
                        <div className="flex-1">
                          <Label htmlFor="save_version_history" className="text-sm font-medium cursor-pointer">
                            Save current version to history (recommended)
                          </Label>
                          <p className="text-xs text-slate-500 mt-1">
                            Creates a snapshot of the current artifact before applying changes. Uncheck to overwrite without saving history.
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Change Description (Optional)</Label>
                        <Input data-testid="art-artifacts-input-7"
                          value={artifactForm.change_description}
                          onChange={(e) => setArtifactForm({ ...artifactForm, change_description: e.target.value })}
                          placeholder="Briefly describe what you changed..."
                          className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                        />
                      </div>
                    </div>
                  )}

                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                    {editingArtifact ? 'Update Artifact' : 'Create Artifact'}
                  </Button>
                </form>

                {editingArtifact && (
                  <div className="mt-8 pt-6 border-t border-slate-800">
                    <ContextualDocuments
                      contextType="Artifact"
                      parentContextId={editingArtifact.id}
                      title="Related Documents"
                    />
                  </div>
                )}
              </DialogContent>
            </ConfirmableDialog>
          </div>

          {artifactViewMode === 'table' ? (
            <ArtifactTableView
              artifacts={visibleArtifacts.filter(artifact => {
                if (!artifact.is_company_secret) return true;
                if (artifact.owner_email === user?.email) return true;
                if ((artifact.editors || []).includes(user?.email)) return true;
                if ((artifact.viewers || []).includes(user?.email)) return true;
                return false;
              })}
              officers={officers}
              user={user}
              isSeniorOfficer={isSeniorOfficer}
              onEdit={handleEditArtifact}
              onDelete={(id) => deleteArtifactMutation.mutate(id)}
              onViewHistory={setVersionHistoryArtifact}
              onApprove={(id) => artifactWorkflow.approve(id)}
              onInstitutionalize={(id) => artifactWorkflow.institutionalize(id)}
              onArchive={(id) => archiveArtifactMutation.mutate(id)}
              onUnarchive={(id) => unarchiveArtifactMutation.mutate(id)}
              showArchived={showArchivedArtifacts}
              deleteConfirm={deleteConfirm}
            />
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleArtifacts.filter(artifact => {
              if (!artifact.is_company_secret) return true;
              if (artifact.owner_email === user?.email) return true;
              if ((artifact.editors || []).includes(user?.email)) return true;
              if ((artifact.viewers || []).includes(user?.email)) return true;
              return false;
            }).map((artifact) => {
              const owner = officers.find((o) => o.user_email === artifact.owner_email);
              const canEdit = artifact.owner_email === user?.email || (artifact.editors || []).includes(user?.email) || isSeniorOfficer;

              return (
                <Card key={artifact.id} className="glass-panel border-none">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-slate-600 mb-2 text-lg font-semibold tracking-tight">{artifact.name}</CardTitle>
                        <div className="flex flex-wrap gap-1 mb-2">
                          <Badge variant="outline" className="text-xs">{artifact.category}</Badge>
                          {getStatusBadge(artifact.status)}
                          {artifact.archived && <Badge className="bg-amber-500/20 text-amber-400 border-none">Archived</Badge>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-7 w-7" 
                          onClick={() => setVersionHistoryArtifact(artifact)}
                          title="View version history"
                        >
                          <History className="w-3 h-3" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEditArtifact(artifact)}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <div className="relative">
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="h-7 w-7 hover:text-red-400" 
                                onClick={() => deleteConfirm.checkAndConfirm(artifact.id, () => deleteArtifactMutation.mutate(artifact.id))}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                              {deleteConfirm.isPendingConfirm(artifact.id) && (
                                <span className="absolute left-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                                  Click again to confirm
                                </span>
                              )}
                            </div>
                          </>
                        )}
                        {canEdit && (
                          artifact.archived ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-amber-400 hover:text-amber-300"
                              onClick={() => unarchiveArtifactMutation.mutate(artifact.id)}
                              title="Unarchive"
                            >
                              <ArchiveRestore className="w-3 h-3" />
                            </Button>
                          ) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 hover:text-amber-400"
                              onClick={() => archiveArtifactMutation.mutate(artifact.id)}
                              title="Archive"
                            >
                              <Archive className="w-3 h-3" />
                            </Button>
                          )
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {artifact.description && (
                      <p className="text-sm text-slate-400">{artifact.description}</p>
                    )}
                    
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {artifact.type === 'uploaded' && <Upload className="w-3 h-3" />}
                      {artifact.type === 'link' && <ExternalLink className="w-3 h-3" />}
                      {artifact.type === 'internal' && <FileText className="w-3 h-3" />}
                      <span>{artifact.type}</span>
                    </div>

                    {artifact.tags && artifact.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {artifact.tags.map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">#{tag}</Badge>
                        ))}
                      </div>
                    )}

                    <div className="text-xs text-slate-500">
                      Owner: {owner?.callsign || artifact.owner_email}
                    </div>

                    {artifact.editors && artifact.editors.length > 0 && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Editors:</div>
                        <div className="flex flex-wrap gap-1">
                          {artifact.editors.map((email) => {
                            const editor = officers.find((o) => o.user_email === email);
                            return (
                              <Badge key={email} variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                {editor?.callsign || email}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {artifact.viewers && artifact.viewers.length > 0 && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Viewers:</div>
                        <div className="flex flex-wrap gap-1">
                          {artifact.viewers.map((email) => {
                            const viewer = officers.find((o) => o.user_email === email);
                            return (
                              <Badge key={email} variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                                {viewer?.callsign || email}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {artifact.file_url && artifact.type === 'uploaded' && (
                      <a 
                        href={artifact.file_url} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-400 hover:text-indigo-300 underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View File
                      </a>
                    )}
                    {artifact.file_url && artifact.type === 'link' && (
                      <a href={artifact.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:text-indigo-300 underline">
                        View Artifact →
                      </a>
                    )}

                    {isSeniorOfficer && artifact.status === 'draft' && (
                      <Button
                        size="sm"
                        className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => artifactWorkflow.approve(artifact.id)}
                        disabled={artifactWorkflow.isApproving}
                      >
                        <ThumbsUp className="w-3 h-3 mr-1" />
                        Approve
                      </Button>
                    )}

                    {isSeniorOfficer && artifact.status === 'approved' && (
                      <Button
                        size="sm"
                        className="w-full h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => artifactWorkflow.institutionalize(artifact.id)}
                        disabled={artifactWorkflow.isInstitutionalizing}
                      >
                        <Award className="w-3 h-3 mr-1" />
                        Institutionalize
                      </Button>
                    )}

                    <div className="pt-3 border-t border-slate-700/50 space-y-1 text-xs text-slate-500">
                      <div className="flex items-center justify-between">
                        <span>Created:</span>
                        <span>
                          {artifact.created_date && format(new Date(artifact.created_date), 'MMM dd, yyyy HH:mm')}
                          {' '}by {(() => {
                            const officer = officers.find((o) => o.user_email === artifact.created_by);
                            if (officer) {
                              const level = Math.floor((officer.total_xp || 0) / 1000) + 1;
                              return `${officer.callsign}, ${officer.rank_title || 'Officer'}, L${level}`;
                            }
                            return artifact.created_by;
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Updated:</span>
                        <span>
                          {artifact.updated_date && format(new Date(artifact.updated_date), 'MMM dd, yyyy HH:mm')}
                          {' '}by {(() => {
                            const updaterEmail = artifact.last_updated_by || artifact.created_by;
                            const officer = officers.find((o) => o.user_email === updaterEmail);
                            if (officer) {
                              const level = Math.floor((officer.total_xp || 0) / 1000) + 1;
                              return `${officer.callsign}, ${officer.rank_title || 'Officer'}, L${level}`;
                            }
                            return updaterEmail;
                          })()}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          )}

          {artifacts.length === 0 ? (
            <Card className="glass-panel border-none">
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No artifacts yet</p>
                <p className="text-sm text-slate-600">Create your first artifact to get started</p>
              </CardContent>
            </Card>
          ) : filteredArtifacts.length === 0 ? (
            <Card className="glass-panel border-none">
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No artifacts match your current filters</p>
                <p className="text-sm text-slate-600">Try adjusting your filters or clearing them.</p>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        {/* RITUALS TAB */}
        <TabsContent value="rituals" className="space-y-4">
          <RitualSearch
            rituals={rituals}
            officers={officers}
            onFilteredResults={setFilteredRituals}
          />

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <p className="text-slate-400 text-sm">
                Showing {visibleRituals.length} of {rituals.length} ritual{rituals.length !== 1 ? 's' : ''}
              </p>
              <Button
                variant={showArchivedRituals ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchivedRituals(!showArchivedRituals)}
                className={`h-7 text-xs ${showArchivedRituals ? 'bg-amber-600 hover:bg-amber-700' : 'border-slate-700 text-slate-400'}`}
              >
                <Archive className="w-3 h-3 mr-1" />
                Archived ({archivedRitualCount})
              </Button>
            </div>
            <ConfirmableDialog open={ritualDialog} onOpenChange={(open) => {
              setRitualDialog(open);
              if (!open) {
                setEditingRitual(null);
                resetRitualForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  New Ritual
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="art-artifacts-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingRitual ? 'Edit Ritual' : 'Create New Ritual'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleRitualSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input data-testid="art-artifacts-input-8"
                      value={ritualForm.name}
                      onChange={(e) => setRitualForm({ ...ritualForm, name: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea data-testid="art-artifacts-textarea-1"
                      value={ritualForm.description}
                      onChange={(e) => setRitualForm({ ...ritualForm, description: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={ritualForm.type} onValueChange={(value) => setRitualForm({ ...ritualForm, type: value })}>
                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="meeting">Meeting</SelectItem>
                        <SelectItem value="standup">Stand-up</SelectItem>
                        <SelectItem value="review">Review</SelectItem>
                        <SelectItem value="all_hands">All Hands</SelectItem>
                        <SelectItem value="workshop">Workshop</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cadence</Label>
                      <Select value={ritualForm.cadence} onValueChange={(value) => setRitualForm({ ...ritualForm, cadence: value })}>
                        <SelectTrigger className="bg-slate-950 border-slate-800">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Bi-weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                          <SelectItem value="annually">Annually</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Duration (minutes)</Label>
                      <Input data-testid="art-artifacts-input-9"
                        type="number"
                        value={ritualForm.duration_minutes}
                        onChange={(e) => setRitualForm({ ...ritualForm, duration_minutes: parseInt(e.target.value) })}
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      />
                    </div>
                  </div>

                  {ritualForm.cadence === 'custom' && (
                    <div className="space-y-2">
                      <Label>Custom Cadence Description</Label>
                      <Input data-testid="art-artifacts-input-10"
                        value={ritualForm.custom_cadence}
                        onChange={(e) => setRitualForm({ ...ritualForm, custom_cadence: e.target.value })}
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                        placeholder="e.g., Every first Monday of the month"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Artifacts Used</Label>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (!ritualForm.artifact_ids.includes(value)) {
                          setRitualForm({
                            ...ritualForm,
                            artifact_ids: [...ritualForm.artifact_ids, value]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue placeholder="Add artifact..." />
                      </SelectTrigger>
                      <SelectContent>
                        {artifacts
                          .filter((a) => !ritualForm.artifact_ids.includes(a.id))
                          .filter((a) => {
                            // Filter out company secret artifacts the user can't access
                            if (!a.is_company_secret) return true;
                            if (a.owner_email === user?.email) return true;
                            if ((a.editors || []).includes(user?.email)) return true;
                            if ((a.viewers || []).includes(user?.email)) return true;
                            return false;
                          })
                          .map((artifact) => (
                          <SelectItem key={artifact.id} value={artifact.id}>
                            {artifact.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2">
                      {ritualForm.artifact_ids.map((id) => {
                        const artifact = artifacts.find((a) => a.id === id);
                        return (
                          <Badge key={id} variant="secondary" className="bg-cyan-500/10 text-cyan-400">
                            {artifact?.name || id}
                            <button
                              type="button"
                              onClick={() => setRitualForm({
                                ...ritualForm,
                                artifact_ids: ritualForm.artifact_ids.filter((aid) => aid !== id)
                              })}
                              className="ml-1 hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  {editingRitual && editingRitual.owner_email === user?.email && (
                    <div className="space-y-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <Label className="text-amber-400">Transfer Ownership</Label>
                      <Select
                        value={ritualForm.owner_email || editingRitual.owner_email}
                        onValueChange={(value) => {
                          setRitualForm({
                            ...ritualForm,
                            owner_email: value
                          });
                        }}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800">
                          <SelectValue placeholder="Select new owner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={editingRitual.owner_email}>
                            {officers.find((o) => o.user_email === editingRitual.owner_email)?.callsign || editingRitual.owner_email} (Current)
                          </SelectItem>
                          {officers.filter((o) => o.user_email !== editingRitual.owner_email).map((officer) => (
                            <SelectItem key={officer.id} value={officer.user_email}>
                              {officer.callsign}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-amber-400">⚠️ Transferring ownership will give full control to the new owner</p>
                    </div>
                  )}

                  <div className="space-y-3">
                    <Label>Game Mode Usage</Label>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-slate-950 rounded-lg">
                        <span className="text-sm">Rank & File</span>
                        <Select
                          value={ritualForm.game_mode_usage.rank_and_file}
                          onValueChange={(value) => setRitualForm({
                            ...ritualForm,
                            game_mode_usage: { ...ritualForm.game_mode_usage, rank_and_file: value }
                          })}
                        >
                          <SelectTrigger className="w-40 bg-slate-900 border-slate-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="required">Required</SelectItem>
                            <SelectItem value="recommended">Recommended</SelectItem>
                            <SelectItem value="optional">Optional</SelectItem>
                            <SelectItem value="not_applicable">Not Applicable</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-slate-950 rounded-lg">
                        <span className="text-sm">Campaign</span>
                        <Select
                          value={ritualForm.game_mode_usage.campaign}
                          onValueChange={(value) => setRitualForm({
                            ...ritualForm,
                            game_mode_usage: { ...ritualForm.game_mode_usage, campaign: value }
                          })}
                        >
                          <SelectTrigger className="w-40 bg-slate-900 border-slate-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="required">Required</SelectItem>
                            <SelectItem value="recommended">Recommended</SelectItem>
                            <SelectItem value="optional">Optional</SelectItem>
                            <SelectItem value="not_applicable">Not Applicable</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-slate-950 rounded-lg">
                        <span className="text-sm">Special Ops</span>
                        <Select
                          value={ritualForm.game_mode_usage.special_ops}
                          onValueChange={(value) => setRitualForm({
                            ...ritualForm,
                            game_mode_usage: { ...ritualForm.game_mode_usage, special_ops: value }
                          })}
                        >
                          <SelectTrigger className="w-40 bg-slate-900 border-slate-800">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="required">Required</SelectItem>
                            <SelectItem value="recommended">Recommended</SelectItem>
                            <SelectItem value="optional">Optional</SelectItem>
                            <SelectItem value="not_applicable">Not Applicable</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Participants</Label>
                    <Select
                      value=""
                      onValueChange={(value) => {
                        if (!ritualForm.participants.includes(value)) {
                          setRitualForm({
                            ...ritualForm,
                            participants: [...ritualForm.participants, value]
                          });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue placeholder="Add participant..." />
                      </SelectTrigger>
                      <SelectContent>
                        {officers.filter((o) => !ritualForm.participants.includes(o.user_email)).map((officer) => (
                          <SelectItem key={officer.id} value={officer.user_email}>
                            {officer.callsign}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2">
                      {ritualForm.participants.map((email) => {
                        const officer = officers.find((o) => o.user_email === email);
                        return (
                          <Badge key={email} variant="secondary" className="bg-purple-500/10 text-purple-400">
                            {officer?.callsign || email}
                            <button
                              type="button"
                              onClick={() => setRitualForm({
                                ...ritualForm,
                                participants: ritualForm.participants.filter((p) => p !== email)
                              })}
                              className="ml-1 hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  {isCommanderInChief && (
                    <div className="space-y-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-start gap-3">
                        <input data-testid="art-artifacts-input-11"
                          type="checkbox"
                          id="ritual_is_company_secret"
                          checked={ritualForm.is_company_secret}
                          onChange={(e) => setRitualForm({ ...ritualForm, is_company_secret: e.target.checked })}
                          className="mt-1 w-4 h-4 text-red-600 bg-slate-950 border-slate-700 rounded"
                        />
                        <div className="flex-1">
                          <Label htmlFor="ritual_is_company_secret" className="text-sm font-medium cursor-pointer text-red-400">
                            Company Secret
                          </Label>
                          <p className="text-xs text-red-300 mt-1">
                            If enabled, only you (owner) and participants can see this ritual. It will be hidden from everyone else.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                    {editingRitual ? 'Update Ritual' : 'Create Ritual'}
                  </Button>
                </form>
              </DialogContent>
            </ConfirmableDialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {visibleRituals.filter(ritual => {
              // If not a company secret, show to everyone
              if (!ritual.is_company_secret) return true;
              
              // If company secret, only show to owner and participants
              if (ritual.owner_email === user?.email) return true;
              if ((ritual.participants || []).includes(user?.email)) return true;
              
              return false;
            }).map((ritual) => {
              const owner = officers.find((o) => o.user_email === ritual.owner_email);
              const canEdit = ritual.owner_email === user?.email || isSeniorOfficer;
              const linkedArtifacts = artifacts.filter((a) => ritual.artifact_ids?.includes(a.id));

              return (
                <Card key={ritual.id} className="glass-panel border-none">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-slate-600 mb-2 text-lg font-semibold tracking-tight">{ritual.name}</CardTitle>
                        <div className="flex flex-wrap gap-1 mb-2">
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {ritual.cadence}
                          </Badge>
                          {getStatusBadge(ritual.status)}
                          {ritual.archived && <Badge className="bg-amber-500/20 text-amber-400 border-none">Archived</Badge>}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleEditRitual(ritual)}>
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <div className="relative">
                            <Button 
                              size="icon" 
                              variant="ghost" 
                              className="h-7 w-7 hover:text-red-400" 
                              onClick={() => deleteConfirm.checkAndConfirm(ritual.id, () => deleteRitualMutation.mutate(ritual.id))}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            {deleteConfirm.isPendingConfirm(ritual.id) && (
                              <span className="absolute left-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                                Click again to confirm
                              </span>
                            )}
                          </div>
                          {ritual.archived ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-amber-400 hover:text-amber-300"
                              onClick={() => unarchiveRitualMutation.mutate(ritual.id)}
                              title="Unarchive"
                            >
                              <ArchiveRestore className="w-3 h-3" />
                            </Button>
                          ) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 hover:text-amber-400"
                              onClick={() => archiveRitualMutation.mutate(ritual.id)}
                              title="Archive"
                            >
                              <Archive className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {ritual.description && (
                      <p className="text-sm text-slate-400">{ritual.description}</p>
                    )}

                    {ritual.duration_minutes && (
                      <div className="text-xs text-slate-500">
                        Duration: {ritual.duration_minutes} minutes
                      </div>
                    )}

                    {linkedArtifacts.length > 0 && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Uses Artifacts:</div>
                        <div className="flex flex-wrap gap-1">
                          {linkedArtifacts.map((artifact) => (
                            <Badge key={artifact.id} variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30">
                              {artifact.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {ritual.game_mode_usage && (
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Game Mode Usage:</div>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span>Rank & File:</span>
                            <Badge className={`text-xs ${ritual.game_mode_usage.rank_and_file === 'required' ? 'bg-red-500/20 text-red-400' : 'bg-slate-500/20 text-slate-400'}`}>
                              {ritual.game_mode_usage.rank_and_file}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>Campaign:</span>
                            <Badge className="text-xs bg-slate-500/20 text-slate-400">
                              {ritual.game_mode_usage.campaign}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span>Special Ops:</span>
                            <Badge className="text-xs bg-slate-500/20 text-slate-400">
                              {ritual.game_mode_usage.special_ops}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-slate-500">
                      By: {owner?.callsign || ritual.owner_email}
                    </div>

                    {isSeniorOfficer && ritual.status === 'draft' && (
                      <Button
                        size="sm"
                        className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => ritualWorkflow.approve(ritual.id)}
                        disabled={ritualWorkflow.isApproving}
                      >
                        <ThumbsUp className="w-3 h-3 mr-1" />
                        Approve
                      </Button>
                    )}

                    {isSeniorOfficer && ritual.status === 'approved' && (
                      <Button
                        size="sm"
                        className="w-full h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => ritualWorkflow.institutionalize(ritual.id)}
                        disabled={ritualWorkflow.isInstitutionalizing}
                      >
                        <Award className="w-3 h-3 mr-1" />
                        Institutionalize
                      </Button>
                    )}

                    <div className="pt-3 border-t border-slate-700/50 space-y-1 text-xs text-slate-500">
                      <div className="flex items-center justify-between">
                        <span>Created:</span>
                        <span>
                          {ritual.created_date && format(new Date(ritual.created_date), 'MMM dd, yyyy HH:mm')}
                          {' '}by {(() => {
                            const officer = officers.find((o) => o.user_email === ritual.created_by);
                            if (officer) {
                              const level = Math.floor((officer.total_xp || 0) / 1000) + 1;
                              return `${officer.callsign}, ${officer.rank_title || 'Officer'}, L${level}`;
                            }
                            return ritual.created_by;
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Updated:</span>
                        <span>
                          {ritual.updated_date && format(new Date(ritual.updated_date), 'MMM dd, yyyy HH:mm')}
                          {' '}by {(() => {
                            const updaterEmail = ritual.last_updated_by || ritual.created_by;
                            const officer = officers.find((o) => o.user_email === updaterEmail);
                            if (officer) {
                              const level = Math.floor((officer.total_xp || 0) / 1000) + 1;
                              return `${officer.callsign}, ${officer.rank_title || 'Officer'}, L${level}`;
                            }
                            return updaterEmail;
                          })()}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {rituals.length === 0 ? (
            <Card className="glass-panel border-none">
              <CardContent className="py-12 text-center">
                <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No rituals yet</p>
                <p className="text-sm text-slate-600">Create your first ritual to establish consistent execution patterns</p>
              </CardContent>
            </Card>
          ) : filteredRituals.length === 0 ? (
            <Card className="glass-panel border-none">
              <CardContent className="py-12 text-center">
                <Calendar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No rituals match your current filters</p>
                <p className="text-sm text-slate-600">Try adjusting your filters or clearing them.</p>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>
      </Tabs>

      <ArtifactVersionHistory
        artifact={versionHistoryArtifact}
        officers={officers}
        open={!!versionHistoryArtifact}
        onClose={() => setVersionHistoryArtifact(null)}
      />
    </div>
  );
}